import { Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { FC, useRef } from 'react';
import LinkWithHref from '../LinkWithHref';
import { PROFILE_PAGE_SECTIONS } from '@utils/constants';
import s from './NavBar.module.scss';

interface NavItemProps {
  section: string;
  textKey: string;
  biName: string;
  dataHook?: string;
  isMobileView?: boolean;
  isActive: boolean;
  count?: number;
  onMobileClick: (
    section: string,
    biName: string,
    scrollIntoView?: () => void,
  ) => void;
  onDesktopClick: (section: string, biName: string) => void;
  // Desktop scroll-spy props
  offset?: number;
  onSetActive?: () => void;
  hashSpy?: boolean;
}

const NavItem: FC<NavItemProps> = ({
  section,
  textKey,
  biName,
  dataHook,
  isMobileView,
  isActive,
  count,
  onMobileClick,
  onDesktopClick,
  offset,
  onSetActive,
  hashSpy,
}) => {
  const { t } = useTranslation();
  const itemRef = useRef<HTMLLIElement>(null);

  const scrollIntoView = () => {
    const item = itemRef.current;
    if (!item) return;

    const container = item.closest('[class*="scrollingWrapper"]');
    if (!container) return;

    const { left: itemLeft, right: itemRight } = item.getBoundingClientRect();
    const { left: containerLeft, right: containerRight } =
      container.getBoundingClientRect();

    // Only scroll if item is cut off
    if (itemLeft < containerLeft || itemRight > containerRight) {
      item.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  };

  const handleClick = () => {
    if (isMobileView) {
      onMobileClick(section, biName, scrollIntoView);
    } else {
      onDesktopClick(section, biName);
    }
  };

  const handleSetActive = () => {
    if (isMobileView) {
      scrollIntoView();
    }
    onSetActive?.();
  };

  return (
    <li ref={itemRef}>
      <Text as="h2">
        <LinkWithHref
          href={`#${section}`}
          className={`${s.navItem} ${isActive ? s.active : ''}`}
          weight="thin"
          size="small"
          // Add scroll-spy props for both desktop and mobile
          {...{
            offset: offset ?? 0,
            onSetActive: handleSetActive,
            smooth: true,
            spy: true,
            to: section,
            hashSpy,
          }}
          {...(dataHook && { 'data-hook': dataHook })}
          onClick={handleClick}
        >
          {section === PROFILE_PAGE_SECTIONS.TEMPLATES &&
          isMobileView &&
          count &&
          count >= 0
            ? `${t(textKey)} (${count})`
            : t(textKey)}
        </LinkWithHref>
      </Text>
    </li>
  );
};

export default NavItem;
